import { TokenAdditionalInfo } from '@cimpress-technology/authfront-sdk-core'
import { TOKEN_CLAIMS, DEFAULT_ANON_REFRESH_TOKEN_LIFETIME_IN_SEC, DEFAULT_USER_REFRESH_TOKEN_LIFETIME_IN_SEC } from '../../common/constants'
import { axiosPost } from './axiosWrapper'

const SESSION_TYPE = {
  ANON: 'anon',
  USER: 'user'
} as const

export const TOKEN_TYPE = {
  Session: 'Session',
  Bearer: 'Bearer'
} as const

interface OauthTokenResponse {
  access_token: string
  access_token_claims?: object
  alternative_access_token?: string
  refresh_token: string
  expires_in: number
  token_type: string
  refresh_token_expires_at: number
}

export interface RefreshTokenResponse {
  accessToken: string
  expiresAt: number
  tokenType: string
  additionalInfo: TokenAdditionalInfo
  refreshToken: string
}

export function decodeJWTPayload (token: string): object {
  const parts = token.split('.')
  if (parts.length !== 3) {
    throw new Error('Invalid JWT token format')
  }
  // Convert base64url to standard base64 before decoding
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
  return JSON.parse(atob(base64))
}

export function getDefaultRefreshTokenExpiresAt (sessionType: string | undefined): number {
  const defaultValue = sessionType === SESSION_TYPE.ANON ? DEFAULT_ANON_REFRESH_TOKEN_LIFETIME_IN_SEC : DEFAULT_USER_REFRESH_TOKEN_LIFETIME_IN_SEC
  const expiresAtDate = new Date()
  expiresAtDate.setTime(Date.now() + defaultValue * 1000)
  return Math.floor(expiresAtDate.getTime() / 1000)
}

export const handleOauthTokenResponse = (response: OauthTokenResponse): RefreshTokenResponse | undefined => {
  const {
    access_token: rawAccessToken,
    access_token_claims: rawAccessTokenClaims,
    alternative_access_token: alternativeAccessToken,
    refresh_token: refreshToken,
    expires_in: expiresAt
  } = response

  try {
    let accessToken: string
    let accessTokenClaims: object | undefined

    if (!alternativeAccessToken) {
      accessToken = rawAccessToken
      accessTokenClaims = rawAccessTokenClaims
    } else {
      accessToken = alternativeAccessToken
      accessTokenClaims = decodeJWTPayload(rawAccessToken)
    }

    const sessionType = (((accessTokenClaims as any)?.[TOKEN_CLAIMS.IS_ANONYMOUS_TOKEN_CLAIM] ?? false) === true ? SESSION_TYPE.ANON : SESSION_TYPE.USER) as any

    // Calculate epoch time for expires_in
    const currentTime = Math.floor(Date.now() / 1000)
    const expiresInEpoch = currentTime + expiresAt
    const additionalInfo: TokenAdditionalInfo = {
      expiresIn: expiresInEpoch,
      accessTokenClaims,
      sessionType,
      tokenType: TOKEN_TYPE.Session,
      refreshTokenExpiresIn: getDefaultRefreshTokenExpiresAt(sessionType)
    }
    return { accessToken, expiresAt, tokenType: TOKEN_TYPE.Session, additionalInfo, refreshToken }
  } catch (error) {
    console.error('Error handling OAuth token response:', error)
    return undefined
  }
}

/**
 * Exchanges a refresh token for a new access token, with automatic retries.
 *
 * @param clientId     OAuth client ID.
 * @param ociBaseUrl   Base URL of the OCI/OAuth service (e.g. https://oauth.cimpress.io).
 * @param refreshToken The refresh token to exchange.
 * @param retryCount   Number of retries on transient failures. Defaults to 1.
 */
export async function exchangeRefreshTokenForAccessToken (
  clientId: string,
  ociBaseUrl: string,
  refreshToken: string,
  retryCount?: number
): Promise<RefreshTokenResponse | undefined> {
  const tokenURL = `${ociBaseUrl}/oauth/token`

  try {
    const raw = await axiosPost<OauthTokenResponse>({
      url: tokenURL,
      payload: {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: clientId
      },
      retryCount
    })

    return handleOauthTokenResponse(raw)
  } catch (error) {
    console.error('Error exchanging refresh token for access token:', error)
    return undefined
  }
}
